/**
 * Wizytówki Google - Landing Page
 * P0: Google Business Profile dla hodowców psów
 * 
 * BRANDING:
 * - Biel: #FFFFFF
 * - Czerń: #0B0B0B
 * - Magenta (akcent): #E6007A
 * - 90% biel/czerń, 10% magenta
 * 
 * SEKCJE (zgodnie ze spec):
 * 1. Hero
 * 2. Jak to działa (3 kroki)
 * 3. Dlaczego wizytówka jest ważna
 * 4. Jakie dane zbieramy
 * 5. Cennik
 * 6. Bezpieczeństwo / zasady Google
 * 7. FAQ
 * 8. CTA końcowe
 */
import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { 
  Phone, Globe, Clock, Shield, ChevronDown, X, Mail, Loader2, Check,
  Search, MapPin, PhoneCall, Home, FileText, Camera, Building2,
  ShieldCheck, Ban, CheckCircle2, Lock
} from 'lucide-react';
import { toast } from 'sonner';
import WizytowkiIcon from '@/components/icons/WizytowkiIcon';

// Use backend URL from env, or fallback to empty string for same-origin requests
import { API_BASE as API } from '@/utils/api';

// Branding colors - DARK MODE
const colors = {
  white: '#FFFFFF',
  black: '#0B0B0B',
  magenta: '#E6007A',
  darkBg: '#0B0B0B',
  darkCard: '#1a1a1a',
  darkBorder: '#2a2a2a',
  textPrimary: '#FFFFFF',
  textSecondary: '#a0a0a0'
};

// FAQ Data
const faqItems = [
  {
    q: 'Czy to są reklamy Google?',
    a: 'Nie. To nie są reklamy i nie jest to Google Ads. Przygotowujemy i porządkujemy Google Wizytówkę, która pojawia się w wynikach wyszukiwania i w Google Mapach.'
  },
  {
    q: 'Czy muszę podawać hasło do Google?',
    a: 'Nie. Nigdy nie prosimy o hasło do konta Google. Dodajesz nas bezpiecznie jako menedżera wizytówki.'
  },
  {
    q: 'Co jeśli nie mam jeszcze wizytówki?',
    a: 'W takim przypadku tworzymy ją od podstaw i przeprowadzamy cały proces zgodnie z zasadami Google.'
  },
  {
    q: 'Co jeśli mam już wizytówkę?',
    a: 'Sprawdzimy ją, uporządkujemy i dostosujemy do aktualnych zasad Google.'
  },
  {
    q: 'Ile trwa przygotowanie wizytówki?',
    a: 'Czas realizacji zależy głównie od weryfikacji po stronie Google. Po naszej stronie prace zaczynają się od razu po otrzymaniu danych i dostępu.'
  },
  {
    q: 'Czy gwarantujecie pozycję w Google?',
    a: 'Nie. Nie składamy obietnic dotyczących pozycji. Działamy zgodnie z zasadami Google i skupiamy się na poprawnym przygotowaniu wizytówki.'
  },
  {
    q: 'Czy opieka miesięczna jest obowiązkowa?',
    a: 'Nie. Opieka miesięczna jest całkowicie opcjonalna. Możesz ją uruchomić w dowolnym momencie po przygotowaniu wizytówki.'
  },
  {
    q: 'Co obejmuje opieka miesięczna?',
    a: 'Monitoring wizytówki, reagowanie na alerty Google oraz aktualizację danych, jeśli zajdzie taka potrzeba.'
  },
  {
    q: 'Czy mogę zrezygnować z opieki w dowolnym momencie?',
    a: 'Tak. Opiekę możesz włączyć lub wyłączyć w dowolnym momencie.'
  },
  {
    q: 'Czy ta usługa jest zgodna z zasadami Google?',
    a: 'Tak. Cała usługa jest realizowana zgodnie z oficjalnymi zasadami Google.'
  }
];

// Exit Intent Popup Component
function ExitIntentPopup({ isOpen, onClose }) {
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email.trim()) return;
    
    setLoading(true);
    try {
      // Use relative URL - works on same domain
      const res = await fetch('/api/wizytowki/guide-request', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: email.trim() })
      });
      
      if (res.ok) {
        setSuccess(true);
        localStorage.setItem('wizytowki_guide_requested', 'true');
      } else {
        toast.error('Coś poszło nie tak. Spróbuj ponownie.');
      }
    } catch (err) {
      console.error('[Popup] Fetch error:', err);
      toast.error('Błąd połączenia.');
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      {/* Backdrop */}
      <div 
        className="absolute inset-0 bg-black/60 backdrop-blur-sm"
        onClick={onClose}
      />
      
      {/* Modal */}
      <div 
        className="relative bg-[#1a1a1a] rounded-2xl max-w-md w-full p-8 shadow-2xl border border-[#2a2a2a]"
        style={{ animation: 'fadeInUp 0.3s ease-out' }}
      >
        <button
          onClick={onClose}
          className="absolute top-4 right-4 text-gray-400 hover:text-white transition-colors"
        >
          <X className="w-6 h-6" />
        </button>
        
        {!success ? (
          <>
            {/* Icon */}
            <div 
              className="w-16 h-16 rounded-2xl flex items-center justify-center mx-auto mb-6"
              style={{ background: `linear-gradient(135deg, ${colors.magenta} 0%, #ff4d94 100%)` }}
            >
              <Mail className="w-8 h-8 text-white" />
            </div>
            
            <h2 className="text-2xl font-bold text-center mb-2 text-white">
              Pobierz bezpłatny poradnik o wizytówkach Google dla hodowców. Dowiesz się jak zwiększyć widoczność swojej hodowli
            </h2>
            
            <p className="text-[#a0a0a0] text-center mb-6">
              Pobierz <strong className="text-white">bezpłatny poradnik</strong> o wizytówkach Google dla hodowców. 
              Dowiesz się jak zwiększyć widoczność swojej hodowli.
            </p>
            
            <form onSubmit={handleSubmit}>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="Twój adres email"
                required
                className="w-full px-4 py-4 bg-[#2a2a2a] border border-[#3a3a3a] rounded-xl mb-4 focus:outline-none focus:ring-2 focus:ring-pink-500 text-center text-white placeholder-gray-500"
              />
              
              <button
                type="submit"
                disabled={loading}
                className="w-full py-4 rounded-xl font-semibold text-white transition-all hover:opacity-90 disabled:opacity-50 flex items-center justify-center gap-2"
                style={{ 
                  background: `linear-gradient(135deg, ${colors.magenta} 0%, #ff4d94 100%)`,
                  boxShadow: '0 4px 15px rgba(230, 0, 122, 0.3)'
                }}
              >
                {loading ? (
                  <>
                    <Loader2 className="w-5 h-5 animate-spin" />
                    Wysyłam...
                  </>
                ) : (
                  'Wyślij mi poradnik'
                )}
              </button>
            </form>
            
            <p className="text-xs text-gray-500 text-center mt-4">
              Bez spamu. Możesz się wypisać w każdej chwili.
            </p>
          </>
        ) : (
          <>
            {/* Success state */}
            <div 
              className="w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-6"
              style={{ backgroundColor: '#10B981' }}
            >
              <Check className="w-8 h-8 text-white" />
            </div>
            
            <h2 className="text-2xl font-bold text-center mb-2 text-white">
              Gotowe!
            </h2>
            
            <p className="text-[#a0a0a0] text-center mb-6">
              Poradnik został wysłany na Twój email. Sprawdź skrzynkę (także spam).
            </p>
            
            <button
              onClick={onClose}
              className="w-full py-4 rounded-xl font-semibold border-2 transition-all hover:bg-gray-50"
              style={{ borderColor: colors.black, color: colors.black }}
            >
              Zamknij
            </button>
          </>
        )}
      </div>
      
      <style>{`
        @keyframes fadeInUp {
          from {
            opacity: 0;
            transform: translateY(20px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
      `}</style>
    </div>
  );
}

export default function WizytowkiLanding() {
  // Exit intent state
  const [showExitPopup, setShowExitPopup] = useState(false);
  const [exitIntentTriggered, setExitIntentTriggered] = useState(false);
  
  // Exit intent detection
  useEffect(() => {
    // Don't show if already requested
    if (localStorage.getItem('wizytowki_guide_requested')) return;
    
    const handleMouseLeave = (e) => {
      if (e.clientY <= 0 && !exitIntentTriggered) {
        setShowExitPopup(true);
        setExitIntentTriggered(true);
      }
    };
    
    document.addEventListener('mouseleave', handleMouseLeave);
    
    // Backup: show after 30 seconds if not triggered
    const timerBackup = setTimeout(() => {
      if (!exitIntentTriggered && !localStorage.getItem('wizytowki_guide_requested')) {
        setShowExitPopup(true);
        setExitIntentTriggered(true);
      }
    }, 30000); // 30 seconds
    
    return () => {
      document.removeEventListener('mouseleave', handleMouseLeave);
      clearTimeout(timerBackup);
    };
  }, [exitIntentTriggered]);

  // SEO - Update document head
  useEffect(() => {
    // Title
    document.title = 'Wizytówka Google dla hodowców psów | ApisAI';
    
    // Meta description
    const metaDesc = document.querySelector('meta[name="description"]');
    if (metaDesc) {
      metaDesc.setAttribute('content', 'Profesjonalne przygotowanie wizytówki Google dla hodowców psów. Widoczność w Google Maps, opinie klientów, łatwy kontakt. Od 450 zł. Zgodnie z zasadami Google.');
    }
    
    // Open Graph
    const setOgMeta = (property, content) => {
      let meta = document.querySelector(`meta[property="${property}"]`);
      if (meta) meta.setAttribute('content', content);
    };
    setOgMeta('og:title', 'Wizytówka Google dla hodowców psów | ApisAI');
    setOgMeta('og:description', 'Profesjonalne przygotowanie wizytówki Google dla hodowców. Widoczność w Google Maps i wyszukiwarce. Od 450 zł.');
    setOgMeta('og:url', 'https://apisai.pl/wizytowki');
    
    // Schema.org JSON-LD for AI crawlers
    const schemaScript = document.createElement('script');
    schemaScript.type = 'application/ld+json';
    schemaScript.id = 'wizytowki-schema';
    schemaScript.textContent = JSON.stringify({
      "@context": "https://schema.org",
      "@type": "Service",
      "name": "Wizytówka Google dla hodowców psów",
      "description": "Profesjonalne przygotowanie i optymalizacja wizytówki Google Business Profile dla hodowców psów. Zwiększ widoczność swojej hodowli w Google Maps i wyszukiwarce.",
      "provider": {
        "@type": "Organization",
        "name": "ApisAI",
        "url": "https://apisai.pl"
      },
      "serviceType": "Google Business Profile Management",
      "areaServed": {
        "@type": "Country",
        "name": "Poland"
      },
      "offers": {
        "@type": "Offer",
        "price": "450",
        "priceCurrency": "PLN",
        "description": "Jednorazowe przygotowanie wizytówki Google"
      },
      "audience": {
        "@type": "Audience",
        "audienceType": "Hodowcy psów"
      },
      "hasOfferCatalog": {
        "@type": "OfferCatalog",
        "name": "Usługi wizytówek Google",
        "itemListElement": [
          {
            "@type": "Offer",
            "itemOffered": {
              "@type": "Service",
              "name": "Jednorazowe przygotowanie wizytówki",
              "description": "Założenie lub optymalizacja wizytówki Google Business Profile"
            }
          },
          {
            "@type": "Offer",
            "itemOffered": {
              "@type": "Service",
              "name": "Opieka miesięczna",
              "description": "Monitoring, aktualizacje i reagowanie na alerty Google"
            }
          }
        ]
      }
    });
    
    // Remove old schema if exists, then add new
    const oldSchema = document.getElementById('wizytowki-schema');
    if (oldSchema) oldSchema.remove();
    document.head.appendChild(schemaScript);
    
    // Cleanup on unmount
    return () => {
      const schema = document.getElementById('wizytowki-schema');
      if (schema) schema.remove();
    };
  }, []);

  return (
    <div className="min-h-screen" style={{ backgroundColor: colors.darkBg, color: colors.textPrimary }}>
      {/* Exit Intent Popup */}
      <ExitIntentPopup 
        isOpen={showExitPopup} 
        onClose={() => setShowExitPopup(false)} 
      />
      
      {/* HERO */}
      <section className="min-h-[80vh] flex items-center justify-center py-20 px-6 relative overflow-hidden">
        {/* Background glow effects */}
        <div 
          className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] rounded-full opacity-20 blur-3xl pointer-events-none"
          style={{ background: `radial-gradient(circle, ${colors.magenta} 0%, transparent 70%)` }}
        />
        <div 
          className="absolute top-1/4 left-1/4 w-[300px] h-[300px] rounded-full opacity-10 blur-3xl pointer-events-none"
          style={{ background: colors.magenta }}
        />
        <div 
          className="absolute bottom-1/4 right-1/4 w-[400px] h-[400px] rounded-full opacity-10 blur-3xl pointer-events-none"
          style={{ background: colors.magenta }}
        />
        
        <div className="max-w-4xl mx-auto text-center relative z-10">
          {/* Logo with glow */}
          <div className="flex items-center justify-center mb-8 relative">
            <div 
              className="absolute w-32 h-32 rounded-full blur-2xl opacity-40"
              style={{ background: colors.magenta }}
            />
            <WizytowkiIcon size={80} darkMode={true} />
          </div>
          
          <h1 
            className="text-4xl md:text-5xl lg:text-6xl font-bold mb-6"
            style={{ 
              color: colors.textPrimary,
              letterSpacing: '-0.02em'
            }}
          >
            <span 
              className="italic"
              style={{ 
                background: 'linear-gradient(135deg, #a855f7 0%, #ec4899 50%, #f43f5e 100%)',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                backgroundClip: 'text'
              }}
            >Google Wizytówki</span> i Google Mapy<br />
            dla hodowców psów
          </h1>
          
          <p className="text-xl md:text-2xl mb-4" style={{ color: colors.textSecondary }}>
            Porządek, bezpieczeństwo i widoczność Twojej hodowli w Google
          </p>
          
          <p className="text-sm md:text-base mb-10" style={{ color: colors.textSecondary }}>
            Zgodnie z zasadami Google. Bez reklam. Bez ryzyka blokad.
          </p>
          
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <Link
              to="/wizytowki/start"
              className="px-8 py-4 rounded-xl font-semibold text-white text-lg transition-all hover:opacity-90 hover:scale-105"
              style={{ 
                background: `linear-gradient(135deg, ${colors.magenta} 0%, #ff4d94 100%)`,
                boxShadow: '0 0 30px rgba(230, 0, 122, 0.5), 0 4px 20px rgba(230, 0, 122, 0.4)'
              }}
              data-testid="cta-hero-primary"
            >
              Zamów przygotowanie wizytówki
            </Link>
            
            <a
              href="#kontakt"
              className="px-8 py-4 rounded-xl font-semibold border-2 text-lg transition-all hover:bg-white hover:text-black"
              style={{ borderColor: colors.textPrimary, color: colors.textPrimary }}
              data-testid="cta-hero-secondary"
            >
              Kontakt
            </a>
          </div>
        </div>
      </section>
      
      {/* JAK TO DZIAŁA */}
      <section className="py-20 px-6" style={{ background: `linear-gradient(180deg, ${colors.darkBg} 0%, ${colors.darkCard} 100%)` }}>
        <div className="max-w-4xl mx-auto">
          <h2 className="text-2xl md:text-3xl font-bold text-center mb-4" style={{ color: colors.textPrimary }}>
            Jak to działa?
          </h2>
          <p className="text-center mb-12" style={{ color: colors.textSecondary }}>
            Prosty proces w trzech krokach
          </p>
          
          <div className="grid md:grid-cols-3 gap-6">
            {[
              { num: '1', title: 'Wypełniasz formularz', desc: 'Podajesz podstawowe dane o hodowli' },
              { num: '2', title: 'My przygotowujemy wizytówkę', desc: 'Tworzymy i optymalizujemy profil' },
              { num: '3', title: 'Profil gotowy w Google', desc: 'Twoja hodowla widoczna w wyszukiwarce i Mapach' }
            ].map((step) => (
              <div 
                key={step.num} 
                className="group p-6 rounded-xl text-center transition-all duration-300 hover:-translate-y-1"
                style={{ 
                  backgroundColor: colors.darkCard,
                  border: `1px solid ${colors.darkBorder}`,
                  boxShadow: '0 2px 12px rgba(0, 0, 0, 0.3)',
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.boxShadow = '0 8px 25px rgba(230, 0, 122, 0.2)';
                  e.currentTarget.style.borderColor = colors.magenta;
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.boxShadow = '0 2px 12px rgba(0, 0, 0, 0.3)';
                  e.currentTarget.style.borderColor = colors.darkBorder;
                }}
              >
                <div 
                  className="w-14 h-14 rounded-xl flex items-center justify-center text-white font-bold text-xl mx-auto mb-4 transition-transform group-hover:scale-110"
                  style={{ 
                    background: `linear-gradient(135deg, ${colors.magenta} 0%, #ff4d94 100%)`,
                    boxShadow: '0 4px 15px rgba(230, 0, 122, 0.3)'
                  }}
                >
                  {step.num}
                </div>
                <h3 className="font-semibold text-lg mb-2" style={{ color: colors.textPrimary }}>{step.title}</h3>
                <p className="text-sm" style={{ color: colors.textSecondary }}>{step.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>
      
      {/* DLACZEGO WIZYTÓWKA JEST WAŻNA */}
      <section className="py-20 px-6" style={{ backgroundColor: colors.darkBg }}>
        <div className="max-w-4xl mx-auto">
          <h2 className="text-2xl md:text-3xl font-bold text-center mb-4" style={{ color: colors.textPrimary }}>
            Dlaczego wizytówka jest ważna?
          </h2>
          <p className="text-center mb-12 max-w-2xl mx-auto" style={{ color: colors.textSecondary }}>
            Wizytówka Google to Twój profil w wyszukiwarce i na Mapach Google
          </p>
          
          <div className="grid md:grid-cols-3 gap-6">
            {[
              { icon: Search, title: 'Wyszukiwarka Google', desc: 'Widoczność przy wyszukiwaniu "hodowla psów + miasto"' },
              { icon: MapPin, title: 'Mapy Google', desc: 'Pin na mapie z danymi kontaktowymi i zdjęciami' },
              { icon: PhoneCall, title: 'Przycisk "Zadzwoń"', desc: 'Jedno kliknięcie do połączenia telefonicznego' }
            ].map((item, i) => (
              <div 
                key={i} 
                className="group p-6 rounded-xl transition-all duration-300 hover:-translate-y-1"
                style={{ 
                  backgroundColor: colors.darkCard,
                  border: `1px solid ${colors.darkBorder}`,
                  boxShadow: '0 2px 12px rgba(0, 0, 0, 0.3)',
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.boxShadow = '0 8px 25px rgba(230, 0, 122, 0.2)';
                  e.currentTarget.style.borderColor = colors.magenta;
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.boxShadow = '0 2px 12px rgba(0, 0, 0, 0.3)';
                  e.currentTarget.style.borderColor = colors.darkBorder;
                }}
              >
                <div 
                  className="w-14 h-14 rounded-xl flex items-center justify-center mb-4"
                  style={{ 
                    background: 'linear-gradient(135deg, rgba(230, 0, 122, 0.2) 0%, rgba(230, 0, 122, 0.1) 100%)',
                    boxShadow: '0 0 20px rgba(230, 0, 122, 0.15)'
                  }}
                >
                  <item.icon className="w-7 h-7" style={{ color: colors.magenta }} />
                </div>
                <h3 className="font-semibold mb-2" style={{ color: colors.textPrimary }}>{item.title}</h3>
                <p className="text-sm" style={{ color: colors.textSecondary }}>{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>
      
      {/* JAKIE DANE ZBIERAMY */}
      <section className="py-20 px-6" style={{ background: `linear-gradient(180deg, ${colors.darkCard} 0%, ${colors.darkBg} 100%)` }}>
        <div className="max-w-4xl mx-auto">
          <h2 className="text-2xl md:text-3xl font-bold text-center mb-4" style={{ color: colors.textPrimary }}>
            Jakie dane zbieramy?
          </h2>
          <p className="text-center mb-12" style={{ color: colors.textSecondary }}>
            Tylko informacje potrzebne do założenia wizytówki w Google
          </p>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4 mb-8">
            {[
              { icon: Home, label: 'Nazwa hodowli', desc: 'Oficjalna nazwa Twojej działalności' },
              { icon: MapPin, label: 'Adres lub obszar działania', desc: 'Lokalizacja lub region obsługi' },
              { icon: Phone, label: 'Numer telefonu', desc: 'Kontakt dla klientów' },
              { icon: FileText, label: 'Opis działalności', desc: 'Czym się zajmujesz' },
              { icon: Camera, label: 'Zdjęcia hodowli i psów', desc: 'Wizualna prezentacja' },
              { icon: Clock, label: 'Godziny kontaktu', desc: 'Opcjonalnie' }
            ].map((item, i) => (
              <div 
                key={i} 
                className="group p-5 rounded-xl transition-all duration-300 hover:-translate-y-1"
                style={{ 
                  backgroundColor: colors.darkCard,
                  border: `1px solid ${colors.darkBorder}`,
                  boxShadow: '0 2px 12px rgba(0, 0, 0, 0.3)',
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.boxShadow = '0 8px 25px rgba(230, 0, 122, 0.2)';
                  e.currentTarget.style.borderColor = colors.magenta;
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.boxShadow = '0 2px 12px rgba(0, 0, 0, 0.3)';
                  e.currentTarget.style.borderColor = colors.darkBorder;
                }}
              >
                <div className="flex items-start gap-4">
                  <div 
                    className="w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0"
                    style={{ 
                      background: 'linear-gradient(135deg, rgba(230, 0, 122, 0.2) 0%, rgba(230, 0, 122, 0.1) 100%)',
                      boxShadow: '0 0 15px rgba(230, 0, 122, 0.1)'
                    }}
                  >
                    <item.icon className="w-6 h-6" style={{ color: colors.magenta }} />
                  </div>
                  <div>
                    <h3 className="font-semibold mb-1" style={{ color: colors.textPrimary }}>{item.label}</h3>
                    <p className="text-sm" style={{ color: colors.textSecondary }}>{item.desc}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
          
          {/* Info box */}
          <div 
            className="max-w-lg mx-auto p-6 rounded-2xl text-center"
            style={{ 
              background: 'linear-gradient(135deg, rgba(230, 0, 122, 0.15) 0%, rgba(230, 0, 122, 0.05) 100%)',
              border: '1px solid rgba(230, 0, 122, 0.3)'
            }}
          >
            <div 
              className="w-14 h-14 rounded-full flex items-center justify-center mx-auto mb-4"
              style={{ backgroundColor: 'rgba(230, 0, 122, 0.2)' }}
            >
              <Shield className="w-7 h-7" style={{ color: colors.magenta }} />
            </div>
            <p style={{ color: colors.textSecondary }}>
              <strong style={{ color: colors.textPrimary }}>Nie prosimy o hasło.</strong>
              <br />
              <span>Dodajesz nas jako menedżera z poziomu swojego konta Google.</span>
            </p>
          </div>
        </div>
      </section>
      
      {/* CENNIK */}
      <section className="py-20 px-6" id="cennik" style={{ backgroundColor: colors.darkBg }}>
        <div className="max-w-4xl mx-auto">
          <h2 className="text-2xl md:text-3xl font-bold text-center mb-4" style={{ color: colors.textPrimary }}>
            Cennik
          </h2>
          <p className="text-center mb-12" style={{ color: colors.textSecondary }}>
            Wybierz opcję dopasowaną do Twoich potrzeb
          </p>
          
          <div className="grid md:grid-cols-2 gap-8 max-w-3xl mx-auto">
            {/* PRZYGOTOWANIE WIZYTÓWKI */}
            <div 
              className="group relative p-8 rounded-2xl transition-all duration-300 hover:-translate-y-2 cursor-pointer"
              style={{ 
                backgroundColor: colors.darkCard,
                border: `1px solid ${colors.darkBorder}`,
                boxShadow: '0 4px 20px rgba(0, 0, 0, 0.3)',
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.boxShadow = '0 20px 40px rgba(230, 0, 122, 0.2)';
                e.currentTarget.style.borderColor = colors.magenta;
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.boxShadow = '0 4px 20px rgba(0, 0, 0, 0.3)';
                e.currentTarget.style.borderColor = colors.darkBorder;
              }}
            >
              {/* Subtle top accent */}
              <div 
                className="absolute top-0 left-8 right-8 h-1 rounded-b-full opacity-50"
                style={{ backgroundColor: colors.textSecondary }}
              />
              
              <div className="pt-4">
                <h3 className="text-xl font-bold mb-2" style={{ color: colors.textPrimary }}>
                  Przygotowanie wizytówki
                </h3>
                <p className="text-sm mb-6" style={{ color: colors.textSecondary }}>Opłata jednorazowa</p>
                
                <div className="mb-6">
                  <span className="text-5xl font-bold" style={{ color: colors.textPrimary }}>450</span>
                  <span className="text-xl font-semibold ml-1" style={{ color: colors.textSecondary }}>zł</span>
                </div>
                
                <p className="text-sm mb-8 leading-relaxed" style={{ color: colors.textSecondary }}>
                  Przygotowanie Google Wizytówki i Google Map zgodnie z zasadami Google.
                </p>
                
                <Link
                  to="/wizytowki/start?plan=one_time"
                  className="block w-full py-4 text-center rounded-xl font-semibold transition-all duration-300 border-2 hover:bg-white hover:text-black"
                  style={{ borderColor: colors.textPrimary, color: colors.textPrimary }}
                  data-testid="cta-pricing-onetime"
                >
                  Wybierz
                </Link>
              </div>
            </div>
            
            {/* OPIEKA MIESIĘCZNA - WYRÓŻNIONY */}
            <div 
              className="group relative p-8 rounded-2xl transition-all duration-300 hover:-translate-y-2 cursor-pointer"
              style={{ 
                background: `linear-gradient(135deg, ${colors.darkCard} 0%, rgba(230, 0, 122, 0.1) 100%)`,
                border: `2px solid ${colors.magenta}`,
                boxShadow: '0 4px 20px rgba(230, 0, 122, 0.2)',
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.boxShadow = '0 20px 40px rgba(230, 0, 122, 0.35)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.boxShadow = '0 4px 20px rgba(230, 0, 122, 0.2)';
              }}
            >
              {/* Gradient top accent */}
              <div 
                className="absolute top-0 left-0 right-0 h-1 rounded-t-2xl"
                style={{ background: `linear-gradient(90deg, ${colors.magenta} 0%, #ff4d94 100%)` }}
              />
              
              {/* Badge */}
              <div 
                className="absolute -top-4 left-1/2 -translate-x-1/2 px-5 py-1.5 text-white text-xs font-bold tracking-wide rounded-full shadow-lg"
                style={{ 
                  background: `linear-gradient(135deg, ${colors.magenta} 0%, #ff4d94 100%)`,
                  boxShadow: '0 4px 15px rgba(230, 0, 122, 0.4)'
                }}
              >
                POLECANE
              </div>
              
              <div className="pt-4">
                <h3 className="text-xl font-bold mb-2" style={{ color: colors.textPrimary }}>
                  Opieka miesięczna
                </h3>
                <p className="text-sm mb-6" style={{ color: colors.textSecondary }}>Opcjonalna usługa po wykonaniu wizytówki</p>
                
                <div className="mb-1">
                  <span className="text-5xl font-bold" style={{ color: colors.magenta }}>150</span>
                  <span className="text-xl font-semibold ml-1" style={{ color: colors.textSecondary }}>zł</span>
                  <span style={{ color: colors.textSecondary }}>/miesiąc</span>
                </div>
                <p className="text-sm mb-6" style={{ color: colors.textSecondary }}>+ 450 zł jednorazowo za przygotowanie</p>
                
                <p className="text-sm font-medium mb-3" style={{ color: colors.textPrimary }}>W ramach opieki:</p>
                <ul className="space-y-2.5 mb-6">
                  {[
                    'Monitoring widoczności w Google i Mapach',
                    'Reagowanie na alerty ze strony Google',
                    'Aktualizacja danych (godziny, opis, kontakt)',
                    'Wsparcie w przypadku zmian lub pytań'
                  ].map((item, i) => (
                    <li key={i} className="flex items-start gap-2.5 text-sm" style={{ color: colors.textSecondary }}>
                      <div 
                        className="w-5 h-5 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5"
                        style={{ backgroundColor: 'rgba(230, 0, 122, 0.2)' }}
                      >
                        <Check className="w-3 h-3" style={{ color: colors.magenta }} />
                      </div>
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>
                
                <p className="text-xs mb-6" style={{ color: colors.textSecondary }}>
                  Opiekę możesz uruchomić w dowolnym momencie po przygotowaniu wizytówki.
                </p>
                
                <Link
                  to="/wizytowki/start?plan=subscription"
                  className="block w-full py-4 text-center rounded-xl font-semibold text-white transition-all duration-300 hover:opacity-90 hover:scale-[1.02]"
                  style={{ 
                    background: `linear-gradient(135deg, ${colors.magenta} 0%, #ff4d94 100%)`,
                    boxShadow: '0 4px 15px rgba(230, 0, 122, 0.3)'
                  }}
                  data-testid="cta-pricing-subscription"
                >
                  Wybierz
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>
      
      {/* BEZPIECZEŃSTWO */}
      {/* BEZPIECZEŃSTWO */}
      <section className="py-20 px-6" style={{ background: `linear-gradient(180deg, ${colors.darkBg} 0%, ${colors.darkCard} 100%)` }}>
        <div className="max-w-4xl mx-auto">
          <h2 className="text-2xl md:text-3xl font-bold text-center mb-4" style={{ color: colors.textPrimary }}>
            Bezpieczeństwo i zasady Google
          </h2>
          <p className="text-center mb-12" style={{ color: colors.textSecondary }}>
            Działamy zgodnie z wytycznymi Google dla profili firmowych
          </p>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 max-w-2xl mx-auto">
            {[
              { icon: ShieldCheck, title: 'Działamy jako menedżer profilu', desc: 'Bezpieczny dostęp bez hasła' },
              { icon: Ban, title: 'Bez reklam Google Ads', desc: 'To nie jest płatna reklama' },
              { icon: CheckCircle2, title: 'Bez zmiany nazwy firmy', desc: 'Zachowujesz pełną kontrolę' },
              { icon: Lock, title: 'Bez dostępu do hasła', desc: 'Nigdy o nie nie prosimy' }
            ].map((item, i) => (
              <div 
                key={i}
                className="group p-5 rounded-xl transition-all duration-300 hover:-translate-y-1 cursor-default"
                style={{ 
                  backgroundColor: colors.darkCard,
                  border: `1px solid ${colors.darkBorder}`,
                  boxShadow: '0 2px 12px rgba(0, 0, 0, 0.3)',
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.boxShadow = '0 8px 25px rgba(230, 0, 122, 0.2)';
                  e.currentTarget.style.borderColor = colors.magenta;
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.boxShadow = '0 2px 12px rgba(0, 0, 0, 0.3)';
                  e.currentTarget.style.borderColor = colors.darkBorder;
                }}
              >
                <div className="flex items-start gap-4">
                  <div 
                    className="w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0"
                    style={{ 
                      background: 'linear-gradient(135deg, rgba(230, 0, 122, 0.2) 0%, rgba(230, 0, 122, 0.1) 100%)',
                      boxShadow: '0 0 15px rgba(230, 0, 122, 0.1)'
                    }}
                  >
                    <item.icon className="w-6 h-6" style={{ color: colors.magenta }} />
                  </div>
                  <div>
                    <h3 className="font-semibold mb-1" style={{ color: colors.textPrimary }}>{item.title}</h3>
                    <p className="text-sm" style={{ color: colors.textSecondary }}>{item.desc}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>
      
      {/* FAQ */}
      <section className="py-20 px-6" style={{ backgroundColor: colors.darkBg }}>
        <div className="max-w-2xl mx-auto">
          <h2 className="text-2xl md:text-3xl font-bold text-center mb-4" style={{ color: colors.textPrimary }}>
            Często zadawane pytania
          </h2>
          <p className="text-center mb-12" style={{ color: colors.textSecondary }}>
            Odpowiedzi na najczęściej zadawane pytania
          </p>
          
          <div className="space-y-3">
            {faqItems.map((item, i) => (
              <details 
                key={i} 
                className="group rounded-xl overflow-hidden transition-all duration-300"
                style={{ 
                  backgroundColor: colors.darkCard,
                  border: `1px solid ${colors.darkBorder}`,
                }}
              >
                <summary 
                  className="flex items-center justify-between p-5 cursor-pointer list-none font-medium transition-colors"
                  style={{ color: colors.textPrimary }}
                >
                  {item.q}
                  <ChevronDown className="w-5 h-5 group-open:rotate-180 transition-transform flex-shrink-0 ml-4" style={{ color: colors.textSecondary }} />
                </summary>
                <div className="px-5 pb-5" style={{ color: colors.textSecondary, borderTop: `1px solid ${colors.darkBorder}` }}>
                  {item.a}
                </div>
              </details>
            ))}
          </div>
        </div>
      </section>
      
      {/* CTA KOŃCOWE */}
      <section className="py-20 px-6" style={{ background: `linear-gradient(135deg, ${colors.magenta}15 0%, ${colors.darkBg} 100%)` }}>
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-2xl md:text-3xl font-bold mb-4" style={{ color: colors.textPrimary }}>
            Gotowy na widoczność w Google?
          </h2>
          <p className="mb-8" style={{ color: colors.textSecondary }}>
            Zamów przygotowanie wizytówki i bądź tam, gdzie szukają Cię klienci.
          </p>
          
          <Link
            to="/wizytowki/start"
            className="inline-block px-8 py-4 rounded-xl font-semibold text-white text-lg transition-all hover:opacity-90 hover:scale-105"
            style={{ 
              background: `linear-gradient(135deg, ${colors.magenta} 0%, #ff4d94 100%)`,
              boxShadow: '0 4px 20px rgba(230, 0, 122, 0.4)'
            }}
            data-testid="cta-footer"
          >
            Zamów przygotowanie wizytówki
          </Link>
        </div>
      </section>
      
      {/* KONTAKT */}
      <section className="py-16 px-6" id="kontakt" style={{ backgroundColor: colors.darkCard }}>
        <div className="max-w-2xl mx-auto text-center">
          <h2 className="text-2xl font-bold mb-4" style={{ color: colors.textPrimary }}>
            Kontakt
          </h2>
          <p className="mb-6" style={{ color: colors.textSecondary }}>
            Masz pytania? Napisz do nas.
          </p>
          <a 
            href="mailto:kontakt@apisai.pl" 
            className="text-lg font-semibold"
            style={{ color: colors.magenta }}
          >
            kontakt@apisai.pl
          </a>
        </div>
      </section>
      
      {/* FOOTER */}
      <footer className="py-8 px-6" style={{ borderTop: `1px solid ${colors.darkBorder}`, backgroundColor: colors.darkBg }}>
        <div className="max-w-4xl mx-auto flex flex-col md:flex-row items-center justify-between gap-4">
          <div className="flex items-center gap-2">
            <WizytowkiIcon size={24} darkMode={true} />
            <span className="font-semibold" style={{ color: colors.textPrimary }}>ApisAI Wizytówki</span>
          </div>
          
          <div className="flex items-center gap-6 text-sm" style={{ color: colors.textSecondary }}>
            <Link to="/regulamin" className="hover:text-white transition-colors">Regulamin</Link>
            <Link to="/polityka-prywatnosci" className="hover:text-white transition-colors">Prywatność</Link>
          </div>
        </div>
      </footer>
    </div>
  );
}
